import type { ControlSelections } from '../../types/controls';
import type { AppliedDashboardFilters } from '../applied/filterParams';
import type { TemplateControls } from '../templateControls';

/** Facets kept apart, because a URL can arrive carrying only some of them. */
export type RememberedFilters = {
  selectionsByControlKey?: ControlSelections;
  scopedInstanceIds?: string[];
  timeRangeOverride?: string;
};

export const NOTHING_REMEMBERED: RememberedFilters = {};

export const filterMemoryKey = (templateId: string | undefined) => ['dashboards', 'filterMemory', templateId] as const;

/** What is remembered can hold a control another dashboard owned, hence the filtering. */
const showableSelections = (remembered: RememberedFilters, controlKeys: string[]): [string, string[]][] =>
  Object.entries(remembered.selectionsByControlKey ?? {}).filter(
    ([controlKey, values]) => values.length > 0 && controlKeys.includes(controlKey),
  );

/** A URL already naming any control describes the view on its own, so nothing blends into it. */
export const missingControlSelections = ({
  appliedControlParams,
  remembered,
  controlKeys,
}: {
  appliedControlParams: ControlSelections;
  remembered: RememberedFilters;
  controlKeys: string[];
}): [string, string[]][] =>
  controlKeys.length === 0 || Object.keys(appliedControlParams).length > 0
    ? []
    : showableSelections(remembered, controlKeys);

/** With the instance scope folded back under its control's key, the single map the API expects. */
export const rememberedSelections = (
  remembered: RememberedFilters,
  { instanceControlKey, controlKeys }: TemplateControls,
): ControlSelections => {
  const scope = remembered.scopedInstanceIds ?? [];
  return {
    ...Object.fromEntries(showableSelections(remembered, controlKeys)),
    ...(instanceControlKey && scope.length > 0 ? { [instanceControlKey]: scope } : {}),
  };
};

/** A remembered deployment wins; a lone accessible one is taken, there being nothing to choose. */
export const missingInstanceScope = ({
  applied,
  remembered,
  instanceControlKey,
  accessibleInstanceIds,
}: {
  applied: AppliedDashboardFilters;
  remembered: RememberedFilters;
  instanceControlKey: string | undefined;
  accessibleInstanceIds: string[];
}): string[] | undefined => {
  if (!instanceControlKey || applied.scopedInstanceIds.length > 0) return undefined;
  if (remembered.scopedInstanceIds?.length) return remembered.scopedInstanceIds;
  return accessibleInstanceIds.length === 1 ? accessibleInstanceIds : undefined;
};

export const filtersToRemember = (
  selections: ControlSelections,
  timeRange: string | undefined,
  { instanceControlKey, controlKeys }: TemplateControls,
): RememberedFilters => ({
  timeRangeOverride: timeRange,
  ...(instanceControlKey ? { scopedInstanceIds: selections[instanceControlKey] ?? [] } : {}),
  selectionsByControlKey: Object.fromEntries(
    Object.entries(selections).filter(
      ([controlKey]) => controlKey !== instanceControlKey && controlKeys.includes(controlKey),
    ),
  ),
});
